import { useCallback, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { trackClientEvent } from 'analytics/analytics'

interface UseNlsOfferSelectionArgs {
  property: App.NlsPropertyMatch
  index: number
  propertyName: string
  location: string
  isInteractive: boolean
  hasOffer: boolean
  nlsOfferParams?: string
  nlsCarouselVariation?: string
}

export function useNlsOfferSelection({
  property,
  index,
  propertyName,
  location,
  isInteractive,
  hasOffer,
  nlsOfferParams,
  nlsCarouselVariation,
}: UseNlsOfferSelectionArgs) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const handleSelect = useCallback(() => {
    if (!isInteractive || !hasOffer) return
    const eventVariation = nlsCarouselVariation ?? 'not_bucketed'

    trackClientEvent({
      subject: 'support_assistant',
      action: `lesa_nls_offer_clicked_${eventVariation}`,
      category: 'logging',
      type: 'interaction',
      optimizelyEventId: '4557425865392128',
      optimizelyEventKey: 'lesa_nls_offer_click',
    })

    dispatch(addChatMessage(intl.formatMessage({
      id: 'KS6rLRpWRSnSvriGfRsK',
      defaultMessage: 'Show me {propertyName}',
      description: 'Chat message sent when user selects a hotel property to view',
    }, { propertyName }), {
      action: 'selectNlsProperty',
      payload: {
        offerId: property.offerId,
        propertyName,
        location,
        matchIndex: index + 1,
      },
    }))

    dispatch(pushWithRegion(`/offer/${property.offerId}`, nlsOfferParams, { fromSupportChat: true }))
  }, [
    dispatch,
    hasOffer,
    index,
    intl,
    isInteractive,
    location,
    nlsCarouselVariation,
    nlsOfferParams,
    property,
    propertyName,
  ])

  const handleCardClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    const target = event.target as HTMLElement | null
    const isNestedInteractiveElement = !!target?.closest('button, [role="button"], input, select, textarea')
    const shouldUseNativeNavigation = event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey

    if (isNestedInteractiveElement || shouldUseNativeNavigation) {
      return
    }

    event.preventDefault()
    handleSelect()
  }, [handleSelect])

  return {
    handleCardClick,
    handleSelect,
  }
}
